import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import useGoogleMap from '../../hooks/useGoogleMap';
import CustomOverlayContent from './CustomOverlayContent';
import {
	coordinatesShape,
	locationShape
} from '../../constants/propTypeShapes';
import {
	DEFAULT_MAP_ZOOM,
	MAP_MIN_HEIGHT,
	MAP_BORDER_RADIUS
} from '../../constants/mapConstants';

const MapSection = ({
	center,
	zoom = DEFAULT_MAP_ZOOM,
	locations = [],
	hoveredTileIndex,
	isLoading = false
}) => {
	const {
		mapRef,
		isMapReady,
		loadError,
		handleMapResize,
		portalContainer,
		tileData,
		markersUpdating,
		closeOverlay
	} = useGoogleMap({
		center,
		zoom,
		locations,
		hoveredTileIndex
	});

	/**
	 * Trigger map resize once the map instance actually exists.
	 * Before isMapReady the container is visibility:hidden so the map
	 * has zero dimensions — resizing at that point is a no-op at best.
	 */
	useEffect(() => {
		if (isMapReady) {
			handleMapResize();
		}
	}, [isMapReady, handleMapResize]);

	/**
	 * Re-trigger map resize when the container transitions from
	 * display:none → visible (e.g. noResults flips to false in Widget).
	 * Google Maps caches zero dimensions while hidden; a ResizeObserver
	 * catches the layout shift generically without threading props.
	 */
	useEffect(() => {
		if (
			!isMapReady ||
			!mapRef.current ||
			typeof ResizeObserver === 'undefined'
		) {
			return undefined;
		}

		const observer = new ResizeObserver(() => {
			handleMapResize();
		});
		observer.observe(mapRef.current);

		return () => observer.disconnect();
	}, [isMapReady, mapRef, handleMapResize]);

	// Show loading overlay when data is being fetched OR markers are
	// transitioning (fitBounds animation).  This masks the viewport
	// jump that occurs when Google Maps repositions to fit new markers.
	const showOverlay = isMapReady && (isLoading || markersUpdating);

	const mapContentClassNames = setClassNames([
		'map-content',
		'w-100',
		'h-100',
		'border',
		'border-2',
		'border-light',
		'rounded',
		'bg-light',
		'd-flex',
		'align-items-center',
		'justify-content-center',
		'position-relative'
	]);

	// Determine content to render
	let mapContent;
	if (loadError) {
		mapContent = (
			<div className="alert alert-danger">Error loading maps</div>
		);
	} else {
		mapContent = (
			<>
				{/* Always render the map div so mapRef is attached and the
				    Map constructor can fire as soon as the SDK loads.
				    The map is always visible once initialized — never hidden.
				    Markers update in-place (Airbnb / Zillow pattern). */}
				<div
					ref={mapRef}
					role="application"
					aria-label="Interactive map showing location results"
					aria-describedby="map-instructions"
					style={{
						width: '100%',
						height: '100%',
						minHeight: MAP_MIN_HEIGHT,
						borderRadius: MAP_BORDER_RADIUS,
						overflow: 'hidden',
						visibility: isMapReady ? 'visible' : 'hidden'
					}}
				/>
				{/* Initial spinner — only shown until the map instance is
				    created. Once the map is up it stays visible forever. */}
				{!isMapReady && (
					<div
						className="spinner-border position-absolute"
						role="status"
					>
						<span className="visually-hidden">Loading map...</span>
					</div>
				)}
				{/* Semi-transparent overlay while data is being fetched.
				    The map remains visible underneath — user can still
				    pan/zoom while new results load. */}
				{showOverlay && (
					<div
						className="position-absolute d-flex align-items-center justify-content-center top-0 start-0 w-100 h-100 rounded pe-none z-1"
						style={{ background: 'rgba(255, 255, 255, 0.45)' }}
						data-testid="map-loading-overlay"
					>
						<div className="spinner-border" role="status">
							<span className="visually-hidden">
								Updating results...
							</span>
						</div>
					</div>
				)}
			</>
		);
	}

	/**
	 * Map container - render map with loading/error states
	 */
	return (
		<div className="map-column flex-shrink-0 d-none d-lg-block">
			<div
				className="map-sticky-container position-sticky w-100 ps-5"
				aria-label="Interactive map of locations"
			>
				<div
					className={mapContentClassNames}
					data-testid="map-container-wrapper"
				>
					{mapContent}
				</div>
				{portalContainer &&
					tileData &&
					ReactDOM.createPortal(
						<CustomOverlayContent
							tileData={tileData}
							onClose={closeOverlay}
						/>,
						portalContainer
					)}
			</div>
		</div>
	);
};

MapSection.propTypes = {
	center: coordinatesShape.isRequired,
	zoom: PropTypes.number,
	locations: PropTypes.arrayOf(locationShape),
	hoveredTileIndex: PropTypes.number,
	isLoading: PropTypes.bool
};

export default MapSection;
